import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import { T, ZONA, MEJA, WARNA_AGEN, RUANG, SOFA, RAPAT } from "./office";
import { B, C, KAYU, KAYU_GELAP, PUTIH, LOGAM, GELAP, Lukisan, JamDinding, Gitar, PapanTulis, PapanLogo, RakBuku, LedRak, Kursi, Pingpong, Sofa, Uap, Printer, Dispenser, Tanaman } from "./perabot";
import { useTeksTekstur, PapanTeks } from "./tekstur";

// Ruangan kantor isometrik: lantai krem berpetak (tekstur, pas ukuran ruangan), tembok putih (jendela, lukisan, jam, gitar, papan logo,
// papan tulis), ruang server kaca dengan LED kelip, meja rapat, lounge sofa, pingpong, pantry (uap kopi), resepsionis, rak buku, printer, dispenser.
export { Kursi, Tanaman } from "./perabot";
const W = RUANG.w / 2, D = RUANG.d / 2, H = RUANG.tinggi;

// Lantai polos (tanpa tekstur → tidak ada kerlip mipmap saat zoom) + garis petak sebagai garis layar 1px (tidak beraliasing)
function Lantai() {
  const garis = useMemo(() => {
    const p = [];
    for (let x = -W; x <= W; x++) p.push([x, 0.01, -D], [x, 0.01, D]);
    for (let z = -D; z <= D; z++) p.push([-W, 0.01, z], [W, 0.01, z]);
    return p;
  }, []);
  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow><planeGeometry args={[RUANG.w, RUANG.d]} /><meshStandardMaterial color="#efe9d8" roughness={1} /></mesh>
      <Line points={garis} segments color="#d9d1b8" lineWidth={1} transparent opacity={.7} />
      <B p={[0, -0.21, 0]} s={[RUANG.w + .6, 0.4, RUANG.d + .6]} c="#d4cdb8" />
    </>
  );
}

function Dinding() {
  const jendelaZ = [-11, -5, 5, 11], jendelaX = [-14, -8, 8, 14];
  const Jendela = () => (<><B p={[0, 0, 0]} s={[2.6, 1.5, 0.05]} c={PUTIH} /><B p={[0, 0, 0.03]} s={[2.3, 1.25, 0.03]} c="#cfe6f4" e="#cfe6f4" ei={.4} /><B p={[0, 0, 0.05]} s={[0.05, 1.25, 0.02]} c={PUTIH} /><B p={[0, -0.8, 0.12]} s={[2.7, 0.06, 0.25]} c={PUTIH} /></>);
  return (
    <>
      <B p={[0, H / 2, -D]} s={[RUANG.w + .3, H, 0.3]} c={PUTIH} />
      <B p={[-W, H / 2, 0]} s={[0.3, H, RUANG.d]} c={PUTIH} />
      <B p={[0, 0.45, D]} s={[RUANG.w + .3, 0.9, 0.12]} c="#d7eaf4" o={.45} />
      <B p={[W, 0.45, 0]} s={[0.12, 0.9, RUANG.d]} c="#d7eaf4" o={.45} />
      <B p={[0, 0.92, D]} s={[RUANG.w + .3, 0.06, 0.16]} c={PUTIH} /><B p={[W, 0.92, 0]} s={[0.16, 0.06, RUANG.d]} c={PUTIH} />
      <B p={[0, 0.1, -D + .16]} s={[RUANG.w, 0.2, 0.03]} c={T.accent} /><B p={[-W + .16, 0.1, 0]} s={[0.03, 0.2, RUANG.d]} c={T.accent} />
      {jendelaX.map(x => <group key={x} position={[x, 1.75, -D + .16]}><Jendela /></group>)}
      {jendelaZ.map(z => <group key={z} position={[-W + .16, 1.75, z]} rotation={[0, Math.PI / 2, 0]}><Jendela /></group>)}
    </>
  );
}

function RuangServer() {
  const tx = useTeksTekstur({ baris: ["SERVER ROOM"], w: 512, h: 128, bg: "#1d2130", warna: "#9fd8ff", ukuran: 58, radius: 24 });
  const rak = [-16.9, -15.6, -14.3];
  return (
    <group>
      <B p={[-15.2, 0.012, -11.2]} s={[5.4, .012, 5.4]} c="#dcdde3" />
      {/* partisi kaca + rangka, pintu kaca */}
      <B p={[-12.5, H / 2 - .2, -11.6]} s={[.08, H - .4, 4.6]} c="#cfe6f4" o={.35} /><B p={[-12.5, H - .25, -11.6]} s={[.16, .1, 4.6]} c={PUTIH} /><B p={[-12.5, .05, -11.6]} s={[.16, .1, 4.6]} c={PUTIH} />
      <B p={[-15.6, H / 2 - .2, -8.5]} s={[4.4, H - .4, .08]} c="#cfe6f4" o={.35} /><B p={[-15.6, H - .25, -8.5]} s={[4.4, .1, .16]} c={PUTIH} /><B p={[-15.6, .05, -8.5]} s={[4.4, .1, .16]} c={PUTIH} />
      <B p={[-12.5, 1.05, -8.9]} s={[.1, 2.1, .8]} c="#9ed4f2" o={.55} /><B p={[-12.5, 1.1, -8.55]} s={[.12, .3, .06]} c={LOGAM} />
      <PapanTeks tekstur={tx} lebar={1.9} tinggi={.48} position={[-15.6, 2.45, -8.42]} />
      {/* rak server berjajar rapi di dinding belakang + 1 rak UPS di dinding kiri */}
      {rak.map(x => <group key={x} position={[x, 0, -13.2]}><B p={[0, 1.05, 0]} s={[1.1, 2.1, .8]} c="#1d2130" /><B p={[0, 1.05, .4]} s={[.95, 1.95, .02]} c="#2a3045" /><LedRak pos={[0, 0, 0]} /></group>)}
      <group position={[-17.2, 0, -10.2]} rotation={[0, Math.PI / 2, 0]}><B p={[0, 0.75, 0]} s={[.9, 1.5, .7]} c="#1d2130" /><LedRak n={4} pos={[0, -.1, -.06]} /></group>
      <C p={[-13.6, .6, -10.2]} a={[.3, .3, 1.2, 16]} c="#e3e5ea" />{/* AC portabel */}
    </group>
  );
}

// Meja rapat bundar (brainstorming) — klik meja → panel Meja Rapat; lampu tengah menyala saat rapat aktif
function MejaRapat({ p, aktif, onKlik }) {
  const lampu = useRef();
  useFrame(({ clock }) => { if (lampu.current) lampu.current.material.emissiveIntensity = aktif ? .6 + .3 * Math.sin(clock.getElapsedTime() * 3) : 0; });
  return (
    <group position={p} onClick={e => { e.stopPropagation(); onKlik?.(); }} onPointerOver={() => (document.body.style.cursor = "pointer")} onPointerOut={() => (document.body.style.cursor = "")}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, .02, 0]} receiveShadow><circleGeometry args={[RAPAT.rKursi + 1, 40]} /><meshStandardMaterial color="#ece6f5" roughness={1} polygonOffset polygonOffsetFactor={-1} /></mesh>
      <C p={[0, .72, 0]} a={[1.9, 1.9, .08, 32]} c={KAYU} /><C p={[0, .36, 0]} a={[.16, .26, .7, 12]} c={PUTIH} /><C p={[0, .03, 0]} a={[.7, .7, .06, 20]} c={PUTIH} />
      <mesh ref={lampu} position={[0, .8, 0]}><cylinderGeometry args={[.45, .45, .08, 24]} /><meshStandardMaterial color={aktif ? "#b9a6ea" : "#e3e5ea"} emissive="#7c5cbf" emissiveIntensity={0} /></mesh>
      {[0, 1, 2, 3].map(i => { const a = i * Math.PI / 2 + Math.PI / 4; return <group key={i} position={[Math.sin(a) * 1.1, .78, Math.cos(a) * 1.1]} rotation={[0, a, 0]}><B p={[0, 0, 0]} s={[.5, .02, .35]} c="#ffffff" /><C p={[.35, .06, 0]} a={[.05, .045, .1, 10]} c="#fff" /></group>; })}
      {Array.from({ length: RAPAT.kursi }, (_, i) => { const a = i * Math.PI * 2 / RAPAT.kursi, x = Math.sin(a) * RAPAT.rKursi, z = Math.cos(a) * RAPAT.rKursi; return <Kursi key={i} p={[x, 0, z]} r={[0, a, 0]} c={aktif ? "#7c5cbf" : T.accent} />; })}
    </group>
  );
}

function Lounge() {
  return (
    <group>
      <B p={[13.5, .012, 10.5]} s={[6.5, .012, 5.2]} c="#e9e2f3" />
      <B p={[14, .4, 9.8]} s={[1.6, .08, .9]} c={KAYU} />{[-.6, .6].map(x => [-.3, .3].map(z => <B key={x + z} p={[14 + x, .18, 9.8 + z]} s={[.08, .36, .08]} c={LOGAM} />))}
      <C p={[13.7, .5, 9.8]} a={[.06, .05, .12, 10]} c={PUTIH} /><B p={[14.3, .47, 9.9]} s={[.3, .04, .22]} c="#de3a30" />
      <B p={[16.4, .55, 8.2]} s={[.8, 1.1, .5]} c={PUTIH} /><Tanaman pos={[16.4, 1.1, 8.2]} tinggi={.7} />
      <B p={[-16.2, .012, 7.5]} s={[3, .012, 4]} c="#fbe9e4" /><B p={[-9, .012, 11.6]} s={[4.2, .012, 3]} c="#fdf1d6" />
      <B p={[-9, .4, 9.7]} s={[1.2, .08, .7]} c={KAYU} /><C p={[-9.2, .5, 9.7]} a={[.06, .05, .12, 10]} c={PUTIH} />
      <Tanaman pos={[16.6, 0, 12.6]} /><Tanaman pos={[10.8, 0, 12.6]} /><Tanaman pos={[-16.5, 0, 10]} />
    </group>
  );
}

// uap kopi: 3 bola kecil naik & memudar
function Pantry() {
  const vending = useRef();
  useFrame(({ clock }) => { if (vending.current) vending.current.material.emissiveIntensity = .35 + (Math.sin(clock.getElapsedTime() * 7) > .92 ? .4 : 0); });
  return (
    <group position={[0, 0, -D + .55]}>
      <group position={[12.2, 0, 0]}><B p={[0, 1, 0]} s={[1.1, 2, .8]} c="#de3a30" /><mesh ref={vending} position={[-.1, 1.2, .41]}><boxGeometry args={[.7, 1.3, .02]} /><meshStandardMaterial color="#1a2a4a" emissive="#4a6ad8" emissiveIntensity={.35} /></mesh>{[0, 1, 2].map(i => <B key={i} p={[-.1, .75 + i * .4, .43]} s={[.6, .03, .01]} c="#fff" />)}{[0, 1, 2].map(i => [0, 1].map(j => <B key={i + "" + j} p={[-.3 + j * .25, .9 + i * .4, .43]} s={[.12, .2, .01]} c={["#f4be34", "#42a060", "#2e58c4"][(i + j) % 3]} />))}<B p={[.42, 1.3, .41]} s={[.14, .5, .02]} c="#333" /></group>
      <group position={[13.8, 0, 0]}><B p={[0, 1, 0]} s={[1.1, 2, .8]} c="#eef0f2" /><B p={[0, 1.3, .41]} s={[1.08, .02, .02]} c={LOGAM} /><B p={[.4, 1.6, .42]} s={[.05, .5, .04]} c={LOGAM} /><B p={[.4, .7, .42]} s={[.05, .8, .04]} c={LOGAM} /></group>
      <group position={[15.8, 0, 0]}><B p={[0, .45, 0]} s={[2.4, .9, .8]} c={PUTIH} /><B p={[0, .92, 0]} s={[2.5, .06, .9]} c={KAYU} />
        <B p={[-.5, 1.2, 0]} s={[.5, .5, .5]} c={GELAP} /><B p={[-.5, 1.18, .26]} s={[.2, .12, .02]} c="#3ad66a" e="#3ad66a" ei={1} /><C p={[-.5, 1.0, .2]} a={[.06, .05, .1, 10]} c="#fff" /><Uap p={[-.5, 1.1, .2]} />
        {[0.3, 0.55, 0.8].map(x => <C key={x} p={[x, 1.02, .1]} a={[.06, .05, .12, 10]} c={["#fff", "#2e58c4", "#f4be34"][Math.round(x * 4) % 3]} />)}
      </group>
    </group>
  );
}

function Resepsionis() {
  return (
    <group position={[14.5, 0, -1.4]}>
      <B p={[0, .55, 0]} s={[3, 1.1, .9]} c={KAYU_GELAP} /><B p={[0, 1.13, 0]} s={[3.2, .06, 1.1]} c={KAYU} /><B p={[0, .7, -.47]} s={[3, .3, .02]} c={T.accent} />
      <B p={[-.8, 1.35, -.1]} s={[.5, .32, .04]} c={GELAP} e="#cfe6f4" ei={.3} /><C p={[.9, 1.2, .1]} a={[.08, .08, .1, 10]} c="#fff" />
      <Tanaman pos={[1.9, 0, .2]} />
    </group>
  );
}

function Zona({ k }) {
  const z = ZONA[k], w = WARNA_AGEN[k];
  const baris = useMemo(() => z.label.split(/,| & /).map(s => s.trim().toUpperCase()).filter(Boolean), [z.label]);
  const tx = useTeksTekstur({ baris: baris.slice(0, 3), w: 640, h: 300, bg: w, warna: "#ffffff", ukuran: baris.length > 2 ? 54 : 64, radius: 50 });
  return (
    <group position={[z.pos[0], 0, z.pos[1]]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow><planeGeometry args={z.size} /><meshStandardMaterial color={z.warna} roughness={1} polygonOffset polygonOffsetFactor={-1} /></mesh>
      <group position={[-z.size[0] / 2 + 0.6, 0, z.size[1] / 2 - 0.5]} rotation={[0, Math.PI / 4, 0]}>
        <C p={[0, 0.03, 0]} a={[0.26, 0.3, 0.06, 20]} c={w} /><C p={[0, 0.75, 0]} a={[0.03, 0.03, 1.5, 8]} c={LOGAM} />
        <PapanTeks tekstur={tx} lebar={1.6} tinggi={0.75} position={[0, 1.8, 0.03]} />
        <B p={[0, 1.8, -0.01]} s={[1.66, 0.81, 0.04]} c="#ffffff" />
      </group>
    </group>
  );
}

// Layar monitor: menyala terang dengan baris "kode" beranimasi + cahaya ke meja saat agen bekerja; gelap saat idle
function Layar({ aktif, warna }) {
  const baris = useRef([]), cahaya = useRef(), kaca = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    baris.current.forEach((m, i) => { if (m) { const v = aktif ? 0.18 + 0.22 * Math.abs(Math.sin(t * 2.3 + i * 1.7)) : 0; m.scale.x = Math.max(v * 3, 0.001); m.position.x = -0.26 + v * 1.5 / 2; m.visible = aktif; } });
    if (cahaya.current) cahaya.current.intensity = aktif ? 1.2 + 0.25 * Math.sin(t * 9) : 0;
    if (kaca.current) kaca.current.material.emissiveIntensity = aktif ? 0.9 + 0.08 * Math.sin(t * 11) : 0.05;
  });
  return (
    <group position={[0, 1.04, -0.84]}>
      <B p={[0, 0, 0]} s={[0.7, 0.44, 0.05]} c={GELAP} />
      <mesh ref={kaca} position={[0, 0, 0.028]}><boxGeometry args={[0.64, 0.38, 0.01]} /><meshStandardMaterial color={aktif ? "#dfe9ff" : "#1a1f2e"} emissive={aktif ? "#9fc3ff" : "#1a1f2e"} emissiveIntensity={aktif ? 0.9 : 0.05} /></mesh>
      {[0, 1, 2, 3, 4].map(i => <mesh key={i} ref={el => (baris.current[i] = el)} position={[-0.26, 0.13 - i * 0.065, 0.036]}><boxGeometry args={[0.5, 0.03, 0.005]} /><meshBasicMaterial color={i % 3 === 0 ? warna : i % 3 === 1 ? "#0000CD" : "#2e9e5b"} /></mesh>)}
      <pointLight ref={cahaya} position={[0, 0, 0.5]} color="#bcd4ff" intensity={0} distance={2.2} />
    </group>
  );
}

// Meja kerja agen: meja kayu, monitor, keyboard, cangkir, buku, kursi
export function MejaKerja({ pos, warna, st }) {
  return (
    <group position={[pos[0], 0, pos[1]]} rotation={[0, pos[2], 0]}>
      <B p={[0, 0.72, -0.6]} s={[1.6, 0.08, 0.74]} c={KAYU} />
      {[-0.68, 0.68].map(x => <B key={x} p={[x, 0.35, -0.6]} s={[0.08, 0.7, 0.64]} c={PUTIH} />)}
      <B p={[0.5, 0.45, -0.62]} s={[0.4, 0.5, 0.6]} c={PUTIH} /><B p={[0.5, 0.5, -0.31]} s={[0.2, 0.03, 0.02]} c={LOGAM} /><B p={[0.5, 0.3, -0.31]} s={[0.2, 0.03, 0.02]} c={LOGAM} />
      <Layar aktif={st === "working" || st === "handoff"} warna={warna} />
      <B p={[0, 0.82, -0.84]} s={[0.16, 0.1, 0.1]} c={LOGAM} /><B p={[0, 0.77, -0.5]} s={[0.46, 0.03, 0.16]} c="#e3e5ea" /><B p={[0.36, 0.77, -0.5]} s={[0.1, 0.03, 0.14]} c="#e3e5ea" />
      <C p={[-0.58, 0.82, -0.5]} a={[0.06, 0.05, 0.12, 10]} c={PUTIH} /><B p={[-0.58, 0.78, -0.82]} s={[0.26, 0.08, 0.2]} c={warna} />
      <Kursi p={[0, 0, 0.3]} r={[0, 0, 0]} />
    </group>
  );
}

export function OfficeRoom({ agen, rapatAktif, onRapat }) {
  return (
    <group>
      <Lantai /><Dinding />
      <PapanLogo p={[0, 2.1, -D + .2]} /><JamDinding p={[3.4, 2.5, -D + .2]} /><PapanTulis p={[-4.3, 1.7, -D + .2]} />
      <Lukisan p={[-11, 1.9, -D + .2]} /><Lukisan p={[11, 1.9, -D + .2]} warna={["#42a060", "#2e58c4", "#ec802e"]} />
      <RakBuku p={[5.6, 0, -D + .4]} /><RakBuku p={[7.2, 0, -D + .4]} />
      <Lukisan p={[-W + .2, 1.9, -1.8]} r={[0, Math.PI / 2, 0]} warna={["#e2679b", "#f4be34", "#2e58c4"]} lebar={1.8} />
      <Gitar p={[-W + .25, 1.6, 1.6]} /><Lukisan p={[-W + .2, 1.9, 8.2]} r={[0, Math.PI / 2, 0]} warna={["#8a56c8", "#6ebe96", "#de3a30"]} />
      <RuangServer /><MejaRapat p={[RAPAT.pos[0], 0, RAPAT.pos[1]]} aktif={rapatAktif} onKlik={onRapat} /><Pingpong p={[-13, 0, 10]} /><Lounge /><Pantry /><Resepsionis />
      <Dispenser p={[-13, 0, 3]} /><Tanaman pos={[-14.2, 0, 3.6]} tinggi={1.2} />
      {SOFA.map(([x, z, r, lebar, c]) => <Sofa key={x + "," + z} p={[x, 0, z]} r={[0, r, 0]} lebar={lebar} c={c} />)}
      <Printer p={[-5.2, 0, -9]} /><Dispenser p={[5.2, 0, 3.6]} /><Printer p={[5.4, 0, -6.2]} />
      {[[-6, -13.2], [-16.8, 12.8], [-6.5, 12.8], [6.5, 12.8], [-5.5, 4.4], [17, -8.5], [17, 6.2], [-17.2, -1.2]].map(([x, z]) => <Tanaman key={x + "," + z} pos={[x, 0, z]} tinggi={1.1} />)}
      {Object.keys(ZONA).map(k => <Zona key={k} k={k} />)}
      {Object.entries(MEJA).map(([k, pos]) => { const a = agen.find(x => x.key === k); return <MejaKerja key={k} pos={pos} warna={WARNA_AGEN[a?.zona || "hub"]} st={a?.status} />; })}
    </group>
  );
}
